import { defineStore } from 'pinia'
import { computed, type Ref, ref, watch } from 'vue'

import { useLoader } from '@/components/composables/loader.ts'
import { DataServiceFactory, EntraIDAuthService, ScenarioSubJobService } from '@/factories'
import type {
	IForecastIndex,
	ILaborWeeklyDistribution,
	IProjectLaborDistribution,
	IScenarioSubJob,
	ISiteDemand,
	IStoreEntity,
	IWeeklyJobHourDistribution
} from '@/interfaces'
import { ScenarioSubJob } from '@/models'
import { EntraIDAuthConfig } from '@/services'
import { type IFullStore, useAuthStore, useBaseStore } from '@/stores'

// Since IFullStore already extends IBaseStore (which includes all filtering capabilities),
// we just need to indicate that ForecastStore supports filtering by extending IFilterableStore
export interface IForecastStore<T extends IStoreEntity, TI = T> extends IFullStore<T, TI> {
	activeScenarios: Ref<IScenarioSubJob[]>
	laborEntries: Ref<IProjectLaborDistribution[]>
	weeklyJobHourDistribution: Ref<IWeeklyJobHourDistribution[]>
	hoursRatio: Ref<number>
	contractValueToggle: Ref<boolean>
	laborDistributionRef: Ref<ILaborWeeklyDistribution[]>
	// get(id: number): Promise<IScenarioSubJob>
	getDemandByWeek(weekOf: string): Promise<ISiteDemand>
	getScenarios(id: number): Promise<IScenarioSubJob[]>
	getBaseline(id: number): Promise<IScenarioSubJob>
	commit(id: number): Promise<IScenarioSubJob>
	getSingleIndex(filter?: object): Promise<IForecastIndex[]>
	remove(id: number): Promise<IScenarioSubJob>
	navigateToCurrentMonth(): Promise<void>
	clearSearch(): void
}

const authService = DataServiceFactory.createAuthService('EntraIDAuth', EntraIDAuthConfig) as EntraIDAuthService
// const forecastService = DataServiceFactory.createService('Forecast', authService) as ForecastService
const scenarioSubJobService = DataServiceFactory.createService('ScenarioSubJob', authService) as ScenarioSubJobService

export const useForecastStore = defineStore<'forecast-store', IForecastStore<IScenarioSubJob, IForecastIndex>>(
	'forecast-store',
	() => {
		const {
			id,
			records,
			activeRecord,
			searchTerm,
			preFilters,
			currentPage,
			sortCriteria,
			storeActive,
			isDuplicating,
			activeRecordSavedHash,
			indexView,
			// indexFilter,
			// indexParams,
			viewList,
			// filterList,
			pageSize,

			elevatedPermissions,
			currentFormHash,
			currentFormDirty,
			preFilteredRecords,
			searchedRecords,
			searchRecordIndex,
			searchedRecordLength,
			currentPages,
			totalPages,
			recordCount,
			previousRecord,
			nextRecord,
			orderedRecords,
			pagedRecords,

			canRead,
			canEdit,
			canAdd,
			clearActive,
			// clearSearch,
			calcHash,

			// Column filter methods
			columnFilters,
			activeColumnFilters,
			hasActiveColumnFilters,
			activeColumnFilterCount,
			setColumnFilter,
			clearColumnFilter,
			clearAllColumnFilters,
			getColumnFilter,
			hasColumnFilter
		} = useBaseStore<IScenarioSubJob, 'Forecast', IForecastIndex>('Forecast')

		const getAuthStore = computed(() => {
			return useAuthStore()
		})

		const loader = useLoader()
		const laborEntries = computed<IProjectLaborDistribution[]>(() => {
			return records.value.map((record) => {
				return record.laborEntries
			})
		})
		const searchedLaborEntries = computed(() => {
			return searchedRecords.value.map((record) => {
				return record.laborEntries
			})
		})

		const weeklyJobHourDistribution = computed<IWeeklyJobHourDistribution[]>(() => {
			const records = searchedRecords.value

			// Early return if no records to process
			if (!records || records.length === 0) {
				return []
			}

			const weeklyRecords: IWeeklyJobHourDistribution[] = []

			// Pre-calculate rounded hours cache to avoid repeated Math.round operations
			const hoursCache = new Map<number, number>()
			const getRoundedHours = (hours: number): number => {
				if (hoursCache.has(hours)) {
					return hoursCache.get(hours)!
				}
				const rounded = Math.round(hours * 100) / 100
				hoursCache.set(hours, rounded)
				return rounded
			}

			records.forEach((record) => {
				const laborDistribution = record.laborEntries

				// Skip records without labor distribution
				if (!laborDistribution?.entries?.length) {
					return
				}

				// Create base record template to avoid repeated property access
				const baseRecord = {
					forecastId: record.forecastId,
					subjobId: record.subjobId,
					subjobNumber: record.subjobNumber,
					subjobName: record.subjobName,
					buildLocation: record.buildLocation,
					salesLocation: record.salesLocation,
					clientId: record.clientId,
					clientName: record.clientName,
					showCode: record.showCode,
					showPrimaryLocation: record.showPrimaryLocation,
					showName: record.showName,
					showNameFull: record.showNameFull, // Add computed getter value
					probabilityOfSales: record.probabilityOfSales,
					hoursProbability: record.hoursProbability,
					estimatedContract: record.estimatedContract,
					estimatedContractSource: record.estimatedContractSource,
					forecastType: record.forecastType,
					isForecast: record.isForecast,
					timeline: record.timeline,
					dueDate: record.dueDate,
					shipDate: record.shipDate,
					productionKickoffDate: record.productionKickoffDate,
					actualSignDate: record.actualSignDate,
					workOrderDate: record.workOrderDate,
					status: record.status,
					salesTeam: record.salesTeam,
					salesTeamName: record.salesTeamName,
					subjobType: record.subjobType,
					subjobContractingState: record.subjobContractingState,
					subjobContractingStateAlias: record.subjobContractingStateAlias,
					subjobProductionState: record.subjobProductionState,
					subjobProductionStateAlias: record.subjobProductionStateAlias,
					laborCalc: record.laborCalc,
					laborCalcDisplay: record.laborCalc === 'linear' ? 'Linear' : 'Historical',
					rfp: record.rfp,
					rfpDisplay: record.rfpDisplay
				}

				// Calculate weighted hours first to use for proportional distribution
				const jobWeightedHours =
					parseFloat(record.probabilityOfSales) >= 0.8 || record.weightedHoursOverride === true
						? record.hours
						: Math.round((record.hours * parseFloat(record.probabilityOfSales) + Number.EPSILON) * 100) /
							100

				// Calculate total entry hours to determine proportions
				const totalEntryHours = laborDistribution.entries.reduce((sum, entry) => sum + entry.hours, 0)

				laborDistribution.entries.forEach((entry) => {
					// Calculate weekly hours as a proportion of the WEIGHTED hours
					// This ensures weekly hours sum to weighted hours, not total hours
					const weeklyHoursProportion = totalEntryHours > 0 ? entry.hours / totalEntryHours : 0
					const weeklyHours = jobWeightedHours * weeklyHoursProportion
					const roundedWeeklyHours = getRoundedHours(weeklyHours)

					const weeklyRecord: IWeeklyJobHourDistribution = {
						...baseRecord,
						id: `${record.forecastId}-${entry.dateString}`,
						weekOf: entry.dateString,
						weekStartDate: entry.date,
						hours: roundedWeeklyHours, // Now based on weighted hours proportion
						totalHours: record.hours, // Original total hours for the entire job
						weightedHours: jobWeightedHours // Calculated weighted hours for the job
					}
					weeklyRecords.push(weeklyRecord)
				})
			})

			return weeklyRecords
		})
		const activeScenarios = ref<IScenarioSubJob[]>([])
		if (sortCriteria.value[0] && sortCriteria.value[0].key === 'name') {
			sortCriteria.value[0] = { key: 'dueDate', reverse: false }
		}

		const indexFilter = ref('allrecords')
		const hoursRatio = ref(0)
		const contractValueToggle = ref(false)

		const laborDistributionRef = ref<ILaborWeeklyDistribution[]>([])

		// Define filter groups for subjob types
		const subjobFilterGroup1Types: string[] = ['Rental', 'Build', 'Events', 'Digital'] // Build-related types
		// const subjobFilterGroup2Types: string[] = [] // Sales-related types - will include all types not in group 1 (TODO: populate when all types are known)

		pageSize.value = 25

		const filterList = ref([
			{ value: 'allrecords', label: 'All Records', break: true },
			{ value: 'portlandBuild', label: 'Portland Build' },
			{ value: 'morganHillBuild', label: 'Morgan Hill Build' },
			{ value: 'hartfordBuild', label: 'Hartford Build' },
			{ value: 'chicagoBuild', label: 'Chicago Build' },
			{ value: 'losAngelesBuild', label: 'Los Angeles Build' },
			{ value: 'northCarolinaBuild', label: 'North Carolina Build' },
			{ value: 'trumbullBuild', label: 'Trumbull Build' },
			{ value: '3rdPartyBuild', label: '3rd Party Build', break: true },
			{ value: 'portlandSales', label: 'Portland Sales' },
			{ value: 'morganHillSales', label: 'Morgan Hill Sales' },
			{ value: 'hartfordSales', label: 'Hartford Sales' },
			{ value: 'chicagoSales', label: 'Chicago Sales' },
			{ value: 'losAngelesSales', label: 'Los Angeles Sales' },
			{ value: 'northCarolinaSales', label: 'North Carolina Sales' },
			{ value: 'trumbullSales', label: 'Trumbull Sales' },
			{ value: '3rdPartySales', label: '3rd Party Sales' }
		])

		// Commented out - no longer needed as we're using local filtering
		// const indexParams = computed(() => {
		// 	if (indexFilter.value === 'allrecords') {
		// 		return {}
		// 	} else if (indexFilter.value === 'portlandBuild') {
		// 		return { buildLocation: 'PDX', sort: 1, subjobTypeFilterGroup: 1, hideClosed: 1 }
		// 	} else if (indexFilter.value === 'morganHillBuild') {
		// 		return { buildLocation: 'MH', sort: 1, subjobTypeFilterGroup: 1, hideClosed: 1 }
		// 	} else if (indexFilter.value === 'hartfordBuild') {
		// 		return { buildLocation: 'HAR', sort: 1, subjobTypeFilterGroup: 1, hideClosed: 1 }
		// 	} else if (indexFilter.value === 'chicagoBuild') {
		// 		return { buildLocation: 'CHI', sort: 1, subjobTypeFilterGroup: 1, hideClosed: 1 }
		// 	} else if (indexFilter.value === 'losAngelesBuild') {
		// 		return { buildLocation: 'LA', sort: 1, subjobTypeFilterGroup: 1, hideClosed: 1 }
		// 	} else if (indexFilter.value === 'northCarolinaBuild') {
		// 		return { buildLocation: 'NC', sort: 1, subjobTypeFilterGroup: 1, hideClosed: 1 }
		// 	} else if (indexFilter.value === '3rdPartyBuild') {
		// 		return { buildLocation: 'OT', sort: 1, subjobTypeFilterGroup: 1, hideClosed: 1 }
		// 	} else if (indexFilter.value === 'portlandSales') {
		// 		return { salesLocation: 'PDX', sort: 2, subjobTypeFilterGroup: 2, hideClosed: 1 }
		// 	} else if (indexFilter.value === 'morganHillSales') {
		// 		return { salesLocation: 'MH', sort: 2, subjobTypeFilterGroup: 2, hideClosed: 1 }
		// 	} else if (indexFilter.value === 'hartfordSales') {
		// 		return { salesLocation: 'HAR', sort: 2, subjobTypeFilterGroup: 2, hideClosed: 1 }
		// 	} else if (indexFilter.value === 'chicagoSales') {
		// 		return { salesLocation: 'CHI', sort: 2, subjobTypeFilterGroup: 2, hideClosed: 1 }
		// 	} else if (indexFilter.value === 'losAngelesSales') {
		// 		return { salesLocation: 'LA', sort: 2, subjobTypeFilterGroup: 2, hideClosed: 1 }
		// 	} else if (indexFilter.value === 'northCarolinaSales') {
		// 		return { salesLocation: 'NC', sort: 2, subjobTypeFilterGroup: 2, hideClosed: 1 }
		// 	} else if (indexFilter.value === '3rdPartySales') {
		// 		return { salesLocation: 'OT', sort: 2, subjobTypeFilterGroup: 2, hideClosed: 1 }
		// 	} else if (indexFilter.value === 'salesForecast') {
		// 		return { subjobTypeFilterGroup: 2 }
		// 	} else {
		// 		return {}
		// 	}
		// })

		// Keep indexParams as a simple empty object for backward compatibility
		const indexParams = computed(() => ({}))

		async function getIndex(): Promise<IForecastIndex[]> {
			// Now fetches all records - filtering is done locally via column filters
			return scenarioSubJobService
				.getIndex({}) // Always fetch all records
				.then((data) => {
					// Single pass filter: exclude closed subjobs AND apply contract value filter
					records.value = data.filter((record) => {
						// Filter out closed subjobs
						const subjobStatus = record.subjobStatus?.toUpperCase()
						if (subjobStatus === 'C' || subjobStatus === 'CLOSED') {
							return false
						}

						// Apply contract value filter if enabled
						if (contractValueToggle.value && record.estimatedContract < 50000) {
							return false
						}

						return true
					})

					// Apply current filter after loading data
					if (indexFilter.value !== 'allrecords') {
						applyIndexFilters()
					}
					return records.value
				})
				.catch((err) => {
					console.error(err)
					records.value = []
					return records.value
				})
		}

		async function getSingleIndex(filter?: object): Promise<IForecastIndex[]> {
			return scenarioSubJobService
				.getIndex(filter)
				.then((data) => {
					const returnVal = data
					return returnVal
				})
				.catch((err) => {
					console.error(err)
					const returnVal = [] as IForecastIndex[]
					return returnVal
				})
		}

		async function get(id: number): Promise<IScenarioSubJob> {
			if (id === 0) {
				return addNew()
			}
			return scenarioSubJobService
				.get(id)
				.then((data) => {
					console.log(data)
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)
					return activeRecord.value
				})
				.catch((err) => {
					console.error(err)
					activeRecord.value = {} as ScenarioSubJob
					return activeRecord.value
				})
		}

		async function remove(id: number): Promise<IScenarioSubJob> {
			return scenarioSubJobService
				.remove(id)
				.then((data) => {
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)
					return activeRecord.value
				})
				.catch((err) => {
					throw err
				})
		}

		async function getScenarios(id: number): Promise<IScenarioSubJob[]> {
			return scenarioSubJobService
				.getScenarios(id)
				.then((data) => {
					activeScenarios.value = data
					return activeScenarios.value
				})
				.catch((err) => {
					console.error(err)
					activeScenarios.value = []
					return activeScenarios.value
				})
		}

		async function getBaseline(id: number): Promise<IScenarioSubJob> {
			return scenarioSubJobService
				.getBaseline(id)
				.then((data) => {
					const result = new ScenarioSubJob(data)
					return result
				})
				.catch((err) => {
					console.error(err)

					return {} as IScenarioSubJob
				})
		}

		// function addNew(): Forecast {
		// 	const record = new Forecast({} as Forecast)
		// 	record.id = 0
		// 	activeRecord.value = record
		// 	activeRecordSavedHash.value = calcHash(record)
		// 	return activeRecord.value
		// }

		// TODO figure out how we want to implement a different 'active record' from scenario vs. forecast
		function addNew(): IScenarioSubJob {
			const record = new ScenarioSubJob({} as IScenarioSubJob)
			record.id = 0
			record.subjobId = 0
			activeRecord.value = record
			activeRecordSavedHash.value = calcHash(record)
			return activeRecord.value
		}

		// async function save(record: Forecast): Promise<Forecast> {
		// 	console.log(record)
		// 	return forecastService
		// 		.update(record)
		// 		.then((data) => {
		// 			activeRecord.value = data
		// 			activeRecordSavedHash.value = calcHash(data)
		// 			return activeRecord.value
		// 		})
		// 		.catch((err) => {
		// 			console.error(err)
		// 			throw err
		// 		})
		// 	// }
		// }

		// TODO figure out how we want to implement a different 'active record' from scenario vs. forecast
		async function save(record: ScenarioSubJob): Promise<ScenarioSubJob> {
			console.log(record)
			return scenarioSubJobService
				.upsert(record)
				.then((data) => {
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)
					return activeRecord.value
				})
				.catch((err) => {
					console.error(err)
					throw err
				})
			// if (record.id === 0 || record.id === undefined) {
			// 	return scenarioSubJobService
			// 		.add(record)
			// 		.then((data) => {
			// 			activeRecord.value = data
			// 			activeRecordSavedHash.value = calcHash(data)
			// 			return activeRecord.value
			// 		})
			// 		.catch((err) => {
			// 			console.error(err)
			// 			throw err
			// 		})
			// } else {
			// 	return scenarioSubJobService
			// 		.update(record)
			// 		.then((data) => {
			// 			console.log(data)
			// 			activeRecord.value = data
			// 			activeRecordSavedHash.value = calcHash(data)
			// 			return activeRecord.value
			// 		})
			// 		.catch((err) => {
			// 			console.error(err)
			// 			throw err
			// 		})
			// }
		}

		async function commit(id: number): Promise<IScenarioSubJob> {
			return scenarioSubJobService
				.commit(id)
				.then((data) => {
					return data
				})
				.catch((err) => {
					throw err
				})
		}

		async function navigateToCurrentMonth(): Promise<void> {
			const today = new Date()
			const month = today.getMonth()
			const year = today.getFullYear()
			const date = new Date(year, month, 1)

			// Find the closest match in pagedRecords
			let closestRecord = null
			let closestDiff = Infinity

			for (const record of orderedRecords.value) {
				const recordDate = new Date(record.dueDate)
				const diff = Math.abs(recordDate.getTime() - date.getTime())

				if (diff < closestDiff) {
					closestDiff = diff
					closestRecord = record
				}
			}

			// Calculate the page number
			if (closestRecord) {
				const index = orderedRecords.value.indexOf(closestRecord)
				// const pageSize = 50 // Assuming a page size of 50, adjust as needed
				currentPage.value = Math.floor(index / pageSize.value) + 1
			}
		}

		async function getDemandByWeek(weekOf: string): Promise<ISiteDemand> {
			const pdxDemand = Math.round(
				laborDistributionRef.value.find((record) => record.weekOf === weekOf && record.shop === 'pdx')
					?.totalShopHoursRequired || 0
			)
			const mhDemand = Math.round(
				laborDistributionRef.value.find((record) => record.weekOf === weekOf && record.shop === 'mh')
					?.totalShopHoursRequired || 0
			)
			const laDemand = Math.round(
				laborDistributionRef.value.find((record) => record.weekOf === weekOf && record.shop === 'la')
					?.totalShopHoursRequired || 0
			)
			const chiDemand = Math.round(
				laborDistributionRef.value.find((record) => record.weekOf === weekOf && record.shop === 'chi')
					?.totalShopHoursRequired || 0
			)
			const ncDemand = Math.round(
				laborDistributionRef.value.find((record) => record.weekOf === weekOf && record.shop === 'nc')
					?.totalShopHoursRequired || 0
			)
			const harDemand = Math.round(
				laborDistributionRef.value.find((record) => record.weekOf === weekOf && record.shop === 'har')
					?.totalShopHoursRequired || 0
			)
			const truDemand = Math.round(
				laborDistributionRef.value.find((record) => record.weekOf === weekOf && record.shop === 'tru')
					?.totalShopHoursRequired || 0
			)

			return {
				weekOf: weekOf,
				portlandDemand: pdxDemand,
				morganHillDemand: mhDemand,
				losAngelesDemand: laDemand,
				chicagoDemand: chiDemand,
				northCarolinaDemand: ncDemand,
				hartfordDemand: harDemand,
				trumbullDemand: truDemand
			} as ISiteDemand
		}

		function clearSearch() {
			searchTerm.value = ''
			currentPage.value = 1
			sortCriteria.value = [{ key: 'subjobName', reverse: false }]
		}

		/**
		 * Apply local column filters based on the selected index filter
		 * This replaces the need for API calls when switching filters
		 */
		function applyIndexFilters() {
			// Clear all existing column filters first
			clearAllColumnFilters()

			// Apply filters based on the selected index filter value
			// Store expects raw values - PrimeTable will determine matchMode based on value type
			switch (indexFilter.value) {
				case 'allrecords':
					// No filters - show all records
					break

				// Build location filters
				case 'portlandBuild':
					// Set raw array values - PrimeTable will use 'in' matchMode for arrays
					setColumnFilter('buildLocation', ['PDX'])
					setColumnFilter('subjobType', subjobFilterGroup1Types)
					// TODO: Add filter for hiding closed items when that field is available
					break
				case 'morganHillBuild':
					setColumnFilter('buildLocation', ['MH'])
					setColumnFilter('subjobType', subjobFilterGroup1Types)
					break
				case 'hartfordBuild':
					setColumnFilter('buildLocation', ['HAR'])
					setColumnFilter('subjobType', subjobFilterGroup1Types)
					break
				case 'chicagoBuild':
					setColumnFilter('buildLocation', ['CHI'])
					setColumnFilter('subjobType', subjobFilterGroup1Types)
					break
				case 'losAngelesBuild':
					setColumnFilter('buildLocation', ['LA'])
					setColumnFilter('subjobType', subjobFilterGroup1Types)
					break
				case 'northCarolinaBuild':
					setColumnFilter('buildLocation', ['NC'])
					setColumnFilter('subjobType', subjobFilterGroup1Types)
					break
				case 'trumbullBuild':
					setColumnFilter('buildLocation', ['TRU'])
					setColumnFilter('subjobType', subjobFilterGroup1Types)
					break
				case '3rdPartyBuild':
					setColumnFilter('buildLocation', ['OT'])
					setColumnFilter('subjobType', subjobFilterGroup1Types)
					break

				// Sales location filters
				case 'portlandSales':
					setColumnFilter('salesLocation', ['PDX'])
					// For sales filters, we want all types EXCEPT the build types
					// Since we don't have all types listed, we'll leave subjobType unfiltered for now
					// This can be enhanced when we have a complete list of subjob types
					break
				case 'morganHillSales':
					setColumnFilter('salesLocation', ['MH'])
					break
				case 'hartfordSales':
					setColumnFilter('salesLocation', ['HAR'])
					break
				case 'chicagoSales':
					setColumnFilter('salesLocation', ['CHI'])
					break
				case 'losAngelesSales':
					setColumnFilter('salesLocation', ['LA'])
					break
				case 'northCarolinaSales':
					setColumnFilter('salesLocation', ['NC'])
					break
				case 'trumbullSales':
					setColumnFilter('salesLocation', ['TRU'])
					break
				case '3rdPartySales':
					setColumnFilter('salesLocation', ['OT'])
					break
				case 'salesForecast':
					// For sales forecast, we might want to filter on specific subjob types
					// This needs clarification on what subjobTypeFilterGroup: 2 includes
					break
			}

			// Reset to first page when filters change
			currentPage.value = 1
		}

		// Watch for indexFilter changes and apply local filters instead of making API calls
		watch(
			() => indexFilter.value,
			() => {
				applyIndexFilters()
				// Navigate to current month if sorting by due date
				if (sortCriteria.value[0] && sortCriteria.value[0].key === 'dueDate') {
					navigateToCurrentMonth()
				}
			}
		)

		// Commented out - now using local filtering instead of API calls for index filters
		// watch(
		// 	() => indexParams.value,
		// 	async () => {
		// 		loader.show()
		// 		await getIndex()
		// 		if (sortCriteria.value[0] && sortCriteria.value[0].key === 'dueDate') {
		// 			await navigateToCurrentMonth()
		// 		} else {
		// 			currentPage.value = 1
		// 		}
		// 		loader.hide()
		// 	}
		// )

		watch(
			() => contractValueToggle.value,
			async () => {
				loader.show()
				currentPage.value = 1
				await getIndex()
				if (sortCriteria.value[0] && sortCriteria.value[0].key === 'dueDate') {
					await navigateToCurrentMonth()
				} else {
					currentPage.value = 1
				}
				loader.hide()
			}
		)

		// if (!records.value.length) {
		// 	getIndex()
		// }

		if (hoursRatio.value === 0) {
			scenarioSubJobService.hoursRatio().then((data) => {
				hoursRatio.value = data
			})
		}

		return {
			id,
			records,
			activeRecord,
			searchTerm,
			preFilters,
			currentPage,
			sortCriteria,
			storeActive,
			isDuplicating,
			activeRecordSavedHash,
			indexView,
			indexFilter,
			indexParams,
			location,
			viewList,
			filterList,
			hoursRatio,

			elevatedPermissions,
			currentFormHash,
			currentFormDirty,
			preFilteredRecords,
			searchedRecords,
			searchRecordIndex,
			searchedRecordLength,
			currentPages,
			totalPages,
			recordCount,
			previousRecord,
			nextRecord,
			orderedRecords,
			pagedRecords,
			getAuthStore,
			activeScenarios,
			laborEntries,
			searchedLaborEntries,
			weeklyJobHourDistribution,
			pageSize,
			contractValueToggle,
			laborDistributionRef,
			subjobFilterGroup1Types,

			commit,
			getScenarios,
			getBaseline,
			clearSearch,
			clearActive,
			get,
			getIndex,
			save,
			addNew,
			canRead,
			canEdit,
			canAdd,
			calcHash,
			remove,
			navigateToCurrentMonth,
			getSingleIndex,
			getDemandByWeek,
			applyIndexFilters,

			// Column filter methods
			columnFilters,
			activeColumnFilters,
			hasActiveColumnFilters,
			activeColumnFilterCount,
			setColumnFilter,
			clearColumnFilter,
			clearAllColumnFilters,
			getColumnFilter,
			hasColumnFilter
		}
	}
)
